import React, { useState } from "react";
import DateRangeField from "../../common/datetime/DateRangeField";

/**
 * Modal editor for editing a single pending simulation message before approval.
 */
const MessageEditor = ({ message, onSave, onClose }) => {
  const [subject, setSubject] = useState(message.subject || "");
  const [contentHtml, setContentHtml] = useState(message.contentHtml || "");
  const [contentPlainText, setContentPlainText] = useState(
    message.contentPlainText || "",
  );
  const [communicationContext, setCommunicationContext] = useState(
    message.communicationContext || "",
  );
  const [sophisticationLevel, setSophisticationLevel] = useState(
    message.sophisticationLevel || 5,
  );
  const [isActive, setIsActive] = useState(message.isActive !== false);
  const [activePeriodType, setActivePeriodType] = useState(
    message.activePeriodType || "ALWAYS",
  );
  const [activeStartMonth, setActiveStartMonth] = useState(
    message.activeStartMonth || "",
  );
  const [activeEndMonth, setActiveEndMonth] = useState(
    message.activeEndMonth || "",
  );
  const [activeStartDate, setActiveStartDate] = useState(
    message.activeStartDate || "",
  );
  const [activeEndDate, setActiveEndDate] = useState(
    message.activeEndDate || "",
  );

  const handleSave = () => {
    onSave(message.uuid, {
      subject,
      contentHtml:
        message.communicationChannel?.toUpperCase() === "EMAIL"
          ? contentHtml
          : null,
      contentPlainText,
      communicationContext,
      sophisticationLevel: parseInt(sophisticationLevel),
      isActive,
      activePeriodType,
      activeStartMonth:
        activePeriodType === "SEASONAL"
          ? parseInt(activeStartMonth) || null
          : null,
      activeEndMonth:
        activePeriodType === "SEASONAL"
          ? parseInt(activeEndMonth) || null
          : null,
      activeStartDate:
        activePeriodType === "EVENT" ? activeStartDate || null : null,
      activeEndDate:
        activePeriodType === "EVENT" ? activeEndDate || null : null,
    });
  };

  const getCharCountClass = (current, max) => {
    const ratio = current / max;
    if (ratio >= 1) return "danger";
    if (ratio >= 0.9) return "warning";
    return "";
  };

  return (
    <div className="message-editor-overlay" onClick={onClose}>
      <div className="message-editor" onClick={(e) => e.stopPropagation()}>
        <div className="message-editor-header">
          <h3>Edit Message</h3>
          <button className="editor-close-btn" onClick={onClose}>
            &times;
          </button>
        </div>

        <div className="message-editor-body">
          {/* Subject */}
          <div className="editor-field">
            <label>Subject</label>
            <input
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              maxLength={255}
            />
            <span
              className={`char-count ${getCharCountClass(subject.length, 255)}`}
            >
              {subject.length}/255
            </span>
          </div>

          {/* Plain Text Content */}
          <div className="editor-field">
            <label>Plain Text Content</label>
            <textarea
              value={contentPlainText}
              onChange={(e) => setContentPlainText(e.target.value)}
              maxLength={512}
              rows={4}
            />
            <span
              className={`char-count ${getCharCountClass(contentPlainText.length, 512)}`}
            >
              {contentPlainText.length}/512
            </span>
          </div>

          {/* HTML Content (email only) */}
          {message.communicationChannel?.toUpperCase() === "EMAIL" && (
            <div className="editor-field">
              <label>HTML Content</label>
              <textarea
                value={contentHtml}
                onChange={(e) => setContentHtml(e.target.value)}
                maxLength={5000}
                rows={6}
                style={{ fontFamily: "monospace", fontSize: "0.82rem" }}
              />
              <span
                className={`char-count ${getCharCountClass(contentHtml.length, 5000)}`}
              >
                {contentHtml.length}/5000
              </span>
            </div>
          )}

          {/* Context and Level */}
          <div style={{ display: "flex", gap: "16px" }}>
            <div className="editor-field" style={{ flex: 2 }}>
              <label>Communication Context (comma-separated)</label>
              <input
                type="text"
                value={communicationContext}
                onChange={(e) => setCommunicationContext(e.target.value)}
              />
            </div>
            <div className="editor-field" style={{ flex: 1 }}>
              <label>Sophistication Level (1-10)</label>
              <input
                type="number"
                min="1"
                max="10"
                value={sophisticationLevel}
                onChange={(e) => setSophisticationLevel(e.target.value)}
              />
            </div>
          </div>

          {/* Active Period Settings */}
          <div className="editor-field-group">
            <div className="active-period-header">
              <div className="editor-field" style={{ flex: 0 }}>
                <label>Active</label>
                <label className="editor-toggle">
                  <input
                    type="checkbox"
                    checked={isActive}
                    onChange={(e) => setIsActive(e.target.checked)}
                  />
                  <span>{isActive ? "Yes" : "No"}</span>
                </label>
              </div>
              <div className="editor-field" style={{ flex: 1 }}>
                <label>Period Type</label>
                <select
                  value={activePeriodType}
                  onChange={(e) => {
                    setActivePeriodType(e.target.value);
                    if (e.target.value !== "SEASONAL") {
                      setActiveStartMonth("");
                      setActiveEndMonth("");
                    }
                    if (e.target.value !== "EVENT") {
                      setActiveStartDate("");
                      setActiveEndDate("");
                    }
                  }}
                >
                  <option value="ALWAYS">Always</option>
                  <option value="SEASONAL">Seasonal (months)</option>
                  <option value="EVENT">Event (specific dates)</option>
                </select>
              </div>
            </div>

            {/* Seasonal month range */}
            {activePeriodType === "SEASONAL" && (
              <div className="active-period-row">
                <div className="editor-field" style={{ flex: 1 }}>
                  <label>Start Month</label>
                  <select
                    value={activeStartMonth}
                    onChange={(e) => setActiveStartMonth(e.target.value)}
                  >
                    <option value="">Select...</option>
                    {[
                      "January",
                      "February",
                      "March",
                      "April",
                      "May",
                      "June",
                      "July",
                      "August",
                      "September",
                      "October",
                      "November",
                      "December",
                    ].map((m, i) => (
                      <option key={i + 1} value={i + 1}>
                        {m}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="editor-field" style={{ flex: 1 }}>
                  <label>End Month</label>
                  <select
                    value={activeEndMonth}
                    onChange={(e) => setActiveEndMonth(e.target.value)}
                  >
                    <option value="">Select...</option>
                    {[
                      "January",
                      "February",
                      "March",
                      "April",
                      "May",
                      "June",
                      "July",
                      "August",
                      "September",
                      "October",
                      "November",
                      "December",
                    ].map((m, i) => (
                      <option key={i + 1} value={i + 1}>
                        {m}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            )}

            {/* Event date range */}
            {activePeriodType === "EVENT" && (
              <div className="active-period-row">
                <DateRangeField
                  legend="Active period"
                  fromLabel="Start date"
                  toLabel="End date"
                  futureAllowed
                  presets={[]}
                  from={activeStartDate || ""}
                  to={activeEndDate || ""}
                  onChange={({ from, to }) => {
                    setActiveStartDate(from);
                    setActiveEndDate(to);
                  }}
                />
              </div>
            )}
          </div>

          {/* HTML Preview */}
          {message.communicationChannel?.toUpperCase() === "EMAIL" &&
            contentHtml && (
              <div className="editor-preview">
                <div className="preview-section">
                  <h4>HTML Preview</h4>
                  <div
                    className="html-preview"
                    dangerouslySetInnerHTML={{ __html: contentHtml }}
                  />
                </div>
                <div className="preview-section">
                  <h4>Plain Text Preview</h4>
                  <div className="plain-text-preview">{contentPlainText}</div>
                </div>
              </div>
            )}
        </div>

        <div className="message-editor-footer">
          <button className="editor-cancel-btn" onClick={onClose}>
            Cancel
          </button>
          <button className="editor-save-btn" onClick={handleSave}>
            Save Changes
          </button>
        </div>
      </div>
    </div>
  );
};

export default MessageEditor;
